import {
  NgClass,
  DOCUMENT,
  isPlatformServer,
  isPlatformBrowser,
  NgIf,
} from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { CategoryTypes } from '../../../models/category-types.model';
import { CardChipsComponent } from '../card/card-chips/card-chips.component';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { UseCasesCardComponent } from '../card/use-cases-card/use-cases-card.component';
import { routes } from '../../../constants/routes';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { Usecase } from '../../../models/usecase.model';
import { finalize, Subscription } from 'rxjs';
import { CardType } from '../../../models/card-type.model';
import { CouldntLoadDataComponent } from '../couldnt-load-data/couldnt-load-data.component';
import { NoRecordFoundComponent } from '../no-record-found/no-record-found.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { UtilityService } from '../../../services/utility.service';
import { CardSkeletonLoaderComponent } from '../../utility/card-skeleton-loader/card-skeleton-loader.component';

@Component({
  selector: 'app-home-usecase',
  standalone: true,
  imports: [
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    UseCasesCardComponent,
    CardChipsComponent,
    NgClass,
    RouterLink,
    NgIf,
    CouldntLoadDataComponent,
    NoRecordFoundComponent,
    CardSkeletonLoaderComponent,
  ],
  templateUrl: './home-usecase.component.html',
  styleUrl: './home-usecase.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeUsecaseComponent
  implements OnInit, OnDestroy, ApiErrorHandler
{
  readonly categoryTypes = CategoryTypes;
  readonly CardType = CardType;
  readonly routes = routes;
  public selectedCategory = this.categoryTypes.trending;
  isUsecasesLoading!: boolean;
  isErrorFetchingUsecases: boolean = false;
  usecases?: Usecase[];
  private fetchUsecasesSubs!: Subscription;
  public description =
    'Access use case library to inspire your next AI innovation using AIKosh resources';
  appliedFilters: any = null;
  sortBy = 'trending:DESC';
  isSwiperInitialized = false;

  @ViewChild('swiperContainerUsecases')
  private swiperContainer!: ElementRef<any>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    public toaster: ToasterService,
    private apiService: ApiService,
    private utilityService: UtilityService
  ) {}

  ngOnInit() {
    this.selectedCategory = this.categoryTypes.trending;
    this.fetchUsecases();
  }

  public fetchUsecases() {
    this.usecases = [];
    this.isUsecasesLoading = true;
    let filterParams;
    if (this.selectedCategory === this.categoryTypes.trending) {
      this.sortBy = 'trending:DESC';
      this.fetchTrendingUsecases();
      return;
    } else if (this.selectedCategory === this.categoryTypes.new) {
      this.sortBy = 'createdAt:DESC';
    }
    filterParams = this.utilityService
      .getFiltersParams(this.appliedFilters)
      .set('sort', this.sortBy);
    this.fetchUsecasesSubs = this.apiService
      .fetchUsecases(1, filterParams)
      .pipe(
        finalize(() => {
          this.isUsecasesLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          this.usecases = resp.data.cards.data;

          if (
            this.usecases?.length &&
            !this.isSwiperInitialized &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            this.swiperContainer.nativeElement?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingUsecases = true;
          apiErrorHandler.call(this, error);
        },
      });
  }

  fetchTrendingUsecases() {
    this.fetchUsecasesSubs = this.apiService
      .fetchFilteredUsecase(this.selectedCategory)
      .pipe(
        finalize(() => {
          this.isUsecasesLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          this.usecases = resp;

          if (
            this.usecases?.length &&
            !this.isSwiperInitialized &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            this.swiperContainer.nativeElement?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingUsecases = true;
          apiErrorHandler.call(this, error);
        },
      });
  }

  private initSwiperCarousel() {
    this.isSwiperInitialized = true;
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,
      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.hero-carousel-arrow--prev',
        nextEl: '.hero-carousel-arrow--next',
      },
      followFinger: true,
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      pagination: {
        el: '.usecase-swiper-pagination ',
        clickable: true,
      },
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.usecases!.length < 4) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.usecases!.length < 3) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.usecases!.length < 2) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
      },
    };

    Object.assign(this.swiperContainer.nativeElement, swiperProps);
    (<SwiperContainer>this.swiperContainer.nativeElement).initialize();
  }

  ngOnDestroy() {
    this.fetchUsecasesSubs.unsubscribe();
  }
}
